import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ParticleBackground } from '../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { Navbar } from '../components/Navbar/Navbar';

const CancellationRefundPolicy: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    overview: true,
    cancellation: true,
    refund: true,
    subscription: true,
    digitalContent: true,
    processing: true,
    exceptions: true,
    dispute: true,
    contact: true,
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Reusable section component
  const PolicySection = ({ 
    id, 
    title, 
    children 
  }: { 
    id: string, 
    title: string, 
    children: React.ReactNode 
  }) => (
    <div className="mb-8 border-b border-border-light dark:border-border-dark pb-6">
      <button 
        className="flex w-full justify-between items-center mb-4 text-left"
        onClick={() => toggleSection(id)}
      >
        <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          {title}
        </h2>
        {expandedSections[id] ? 
          <ChevronUp className="text-text-secondary-light dark:text-text-secondary-dark" /> : 
          <ChevronDown className="text-text-secondary-light dark:text-text-secondary-dark" />
        }
      </button>
      {expandedSections[id] && (
        <div className="text-base text-text-secondary-light dark:text-text-secondary-dark space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      {!isAuthenticated && <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar/>
      </div>}

      <div className="container mx-auto px-4 py-12 max-w-6xl mt-20">
          <PageTransitionItem index={1}>
            <div className="mb-12">
              <h1 className="text-3xl md:text-3xl font-heading font-bold mb-4">Cancellation and Refund Policy</h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">
                Last updated: June 24, 2025
              </p>
            </div>
          </PageTransitionItem>
        
        <PageTransitionItem index={2}>
          <div className="w-full bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 mb-8 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
            <PolicySection id="overview" title="Overview">
              <p>
                At Mocktrail, operated by Aurivance Technologies LLP, we strive to provide the best AI-powered interview practice experience. We understand that sometimes plans change, and we want to ensure our cancellation and refund process is fair and transparent.
              </p>
              <p>
                This Cancellation and Refund Policy outlines the terms and conditions for subscription cancellations, refund requests, and dispute resolution for our digital interview practice platform.
              </p>
              <p>
                By subscribing to Mocktrail services, you agree to the terms outlined in this policy. We reserve the right to update this policy at any time, and changes will be effective immediately upon posting.
              </p>
            </PolicySection>
            
            <PolicySection id="subscription" title="Subscription Management">
              <p>
                Mocktrail offers subscription-based access to our AI interview practice platform with the following options:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Monthly Subscriptions:</strong> Billed monthly and automatically renew unless cancelled.</li>
                <li><strong>Quarterly Subscriptions:</strong> Billed every 3 months with cost savings compared to monthly billing.</li>
                <li><strong>Yearly Subscriptions:</strong> Billed annually with maximum cost savings and additional benefits.</li>
                <li><strong>Free Plan:</strong> Limited access with no billing - no cancellation needed.</li>
              </ul>
              <p>
                You can manage your subscription, view billing history, and cancel your plan at any time through your account settings. Cancellation takes effect at the end of your current billing period.
              </p>
              <p>
                <strong>Important:</strong> Cancelling your subscription will prevent future charges but will not trigger an automatic refund for the current billing period unless you qualify under our refund policy outlined below.
              </p>
            </PolicySection>
            
            <PolicySection id="cancellation" title="Cancellation Policy">
              <p>
                We believe in providing flexibility to our users. Here are the details of our cancellation policy:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Immediate Cancellation:</strong> You can cancel your subscription at any time through your account dashboard or by contacting our support team.</li>
                <li><strong>No Cancellation Fees:</strong> We do not charge any fees for cancelling your subscription.</li>
                <li><strong>Service Continuation:</strong> After cancellation, you will continue to have access to premium features until the end of your current billing period.</li>
                <li><strong>Data Retention:</strong> Your account data, interview history, and progress will be retained for 30 days after cancellation in case you decide to reactivate.</li>
                <li><strong>Reactivation:</strong> You can reactivate your subscription at any time, and if within 30 days, your previous data will be restored.</li>
              </ul>
              <p>
                To cancel your subscription, log into your account, navigate to Subscription &gt; Usage History, and click "Cancel Plan." You will be asked to confirm the cancellation.
              </p>
            </PolicySection>
            
            <PolicySection id="refund" title="Refund Policy">
              <p>
                Our refund policy is designed to be fair while protecting against abuse of our digital service:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Technical Issues:</strong> If you experience significant technical problems that prevent you from using our service, and our support team cannot resolve them within 48 hours, you may be eligible for a prorated refund.</li>
                <li><strong>Billing Errors:</strong> If you are charged incorrectly due to a system error, we will provide a full refund of the incorrect charge.</li>
                <li><strong>Duplicate Charges:</strong> If you are accidentally charged multiple times for the same subscription, we will refund the duplicate charges immediately.</li>
                <li><strong>Unused Subscription Time:</strong> For annual or quarterly subscriptions cancelled within 30 days of purchase with minimal usage (less than 2 interview sessions), a prorated refund may be considered on a case-by-case basis.</li>
              </ul>
              <p>
                <strong>Note:</strong> Refunds are generally not provided for partial billing periods if you have actively used the service, as our digital content is delivered immediately upon purchase.
              </p>
            </PolicySection>
            
            <PolicySection id="digitalContent" title="Digital Content and Usage">
              <p>
                As Mocktrail provides digital services that are consumed immediately upon access, special considerations apply:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Immediate Access:</strong> Once you subscribe, you gain immediate access to all premium features, AI feedback, and interview simulations.</li>
                <li><strong>Usage Tracking:</strong> We track your usage to determine eligibility for refunds. Significant usage (more than 5 interview sessions or 60 minutes of platform use) may affect refund eligibility.</li>
                <li><strong>AI-Generated Content:</strong> All AI feedback, analysis, and personalized recommendations are considered consumed digital content.</li>
                <li><strong>Interview Minutes:</strong> Used interview minutes cannot be refunded as they represent consumed digital services.</li>
                <li><strong>Downloaded Content:</strong> Any reports, feedback, or analysis downloaded or viewed are considered delivered digital products.</li>
              </ul>
            </PolicySection>
            
            <PolicySection id="processing" title="Refund Processing">
              <p>
                When a refund is approved, here's what you can expect:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Processing Time:</strong> Approved refunds will be processed within 5-7 business days from the date of approval.</li>
                <li><strong>Refund Method:</strong> Refunds will be credited back to the original payment method used for the subscription purchase.</li>
                <li><strong>Bank Processing:</strong> Depending on your bank or payment provider, it may take an additional 3-5 business days for the refund to appear in your account.</li>
                <li><strong>Confirmation:</strong> You will receive an email confirmation once the refund has been processed from our end.</li>
                <li><strong>Partial Refunds:</strong> In cases of prorated refunds, the amount will be calculated based on unused service time and usage patterns.</li>
              </ul>
              <p>
                <strong>International Payments:</strong> For international transactions, currency conversion rates at the time of refund processing will apply, and additional processing time may be required.
              </p>
            </PolicySection>
            
            <PolicySection id="exceptions" title="Non-Refundable Situations">
              <p>
                While we strive to be fair, certain situations are not eligible for refunds:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Policy Violations:</strong> Accounts terminated for violating our Terms of Service or engaging in fraudulent activity.</li>
                <li><strong>Extended Usage:</strong> Subscriptions with extensive usage (more than 50% of available features or interview minutes used) after the 7-day period.</li>
                <li><strong>Renewal Periods:</strong> Auto-renewed subscriptions where service was actively used during the renewal period (unless cancelled within 48 hours of renewal).</li>
                <li><strong>Free Trial Abuse:</strong> Accounts that repeatedly create free trials or attempt to game the system.</li>
                <li><strong>Third-Party Issues:</strong> Refunds are not provided for issues caused by third-party payment processors, browser incompatibilities, or user error.</li>
                <li><strong>Change of Mind:</strong> Simple change of mind after significant platform usage is not grounds for a refund.</li>
              </ul>
            </PolicySection>
            
            <PolicySection id="dispute" title="Dispute Resolution">
              <p>
                If you have concerns about billing or refunds, we encourage you to contact us first:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Direct Contact:</strong> Reach out to our support team with detailed information about your concern.</li>
                <li><strong>Documentation:</strong> Provide any relevant screenshots, transaction IDs, or error messages.</li>
                <li><strong>Response Time:</strong> We aim to respond to all refund requests within 24-48 hours.</li>
                <li><strong>Resolution Timeline:</strong> Most issues are resolved within 3-5 business days of initial contact.</li>
                <li><strong>Escalation:</strong> If you're not satisfied with the initial response, you can escalate to our management team.</li>
              </ul>
              <p>
                We are committed to fair resolution of all disputes and will work with you to find a satisfactory solution within the bounds of this policy.
              </p>
            </PolicySection>
            
            <PolicySection id="contact" title="Contact Information">
              <p>
                For cancellation requests, refund inquiries, or any questions about this policy, please contact us:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Company:</strong> Aurivance Technologies LLP</li>
                <li><strong>Email:</strong> billing@mocktrail.app, support@mocktrail.app</li>
                <li><strong>Customer Support:</strong> office@aurivancetech.com</li>
                <li><strong>Phone:</strong> +91 7004297500, +91 9877253751</li>
                <li><strong>Address:</strong> 1035 1st floor, Global City Sector 124, Kharar(Rupnagar), Punjab, India, 140301</li>
                <li><strong>Website:</strong> https://mocktrail.app</li>
                <li><strong>Support Hours:</strong> Monday to Friday, 9:00 AM to 6:00 PM IST</li>
              </ul>
              <p>
                When contacting us regarding refunds, please include your subscription email, transaction ID (if available), and a detailed explanation of your request for faster processing.
              </p>
            </PolicySection>
          </div>
        </PageTransitionItem>
      </div>  
    </div>
  );
};

CancellationRefundPolicy.displayName = 'CancellationRefundPolicy';

// Export with page transition HOC
export default withPageTransition(CancellationRefundPolicy);